import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react/popover';
import { Menu } from '@heroui/react/menu';
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/utils/cn';
import { SUPPORTED_LANGUAGES } from '@/i18n';

/**
 * The shell's language menu, on HeroUI's Popover/Menu (same build as
 * `auth/AuthLanguageSwitcher`), so it gets Escape, arrow keys and focus
 * restoration, and its popover flips under RTL.
 *
 * On phones it is a globe icon alone: the full "English ⌄" label took a third
 * of a 360px top bar and truncated the page title to "My …". The label comes
 * back from `sm` up, where there is room for it.
 */
export function LanguageSwitcher() {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();

  const current =
    SUPPORTED_LANGUAGES.find((code) => code === language) ??
    SUPPORTED_LANGUAGES[0];

  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={t('auth.changeLanguage')}
        className={cn(
          'flex h-control min-w-control shrink-0 items-center justify-center gap-1 rounded-md px-2',
          'text-small font-medium text-secondary-500 transition-colors duration-(--shell-motion-fast)',
          'hover:bg-surface-2 hover:text-secondary-800',
          'outline-none focus-visible:ring-2 focus-visible:ring-primary/60',
        )}
      >
        <Globe aria-hidden className="size-icon shrink-0" />
        <span className="hidden sm:inline">
          {t(`common.languageNames.${current}`)}
        </span>
        <ChevronDown
          aria-hidden
          className="hidden size-4 shrink-0 text-secondary-400 sm:block"
        />
      </Popover.Trigger>

      <Popover.Content placement="bottom end" className="min-w-40 p-1">
        <Menu aria-label={t('auth.changeLanguage')}>
          {SUPPORTED_LANGUAGES.map((code) => (
            <Menu.Item
              key={code}
              onAction={() => changeLanguage(code)}
              className="flex items-center gap-2 text-small"
            >
              <Check
                aria-hidden
                className={cn(
                  'size-4 shrink-0',
                  code === language ? 'text-primary-600' : 'opacity-0',
                )}
              />
              {t(`common.languageNames.${code}`)}
            </Menu.Item>
          ))}
        </Menu>
      </Popover.Content>
    </Popover.Root>
  );
}
